import { useCallback, useState } from 'react'
import type { CoreDrawingState, HashAlignment, Tool } from '@/types'

export interface CanvasDrawingState extends CoreDrawingState {
	hashAlignment: HashAlignment
	showColorPicker: boolean
	showDrawOptions: boolean
}

/**
 * Hook for managing canvas drawing state
 * Extracts canvas-related state from ConceptDialog component
 */
export function useCanvasDrawing() {
	const [state, setState] = useState<CanvasDrawingState>({
		tool: 'select',
		color: '#000000',
		brushSize: 7,
		lineStyle: 'solid',
		lineEnd: 'arrow',
		pathMode: 'sharp',
		hashAlignment: 'middle',
		showColorPicker: false,
		showDrawOptions: false,
	})

	const updateState = useCallback((updates: Partial<CanvasDrawingState>) => {
		setState(prev => ({ ...prev, ...updates }))
	}, [])

	const setTool = useCallback((tool: Tool) => {
		setState(prev => ({
			...prev,
			tool,
			showDrawOptions: tool === 'draw',
		}))
	}, [])

	const setColor = useCallback((color: string) => {
		setState(prev => ({ ...prev, color }))
	}, [])

	const setBrushSize = useCallback((brushSize: number) => {
		setState(prev => ({ ...prev, brushSize }))
	}, [])

	const setLineStyle = useCallback((lineStyle: 'solid' | 'dashed') => {
		setState(prev => ({ ...prev, lineStyle }))
	}, [])

	const setLineEnd = useCallback((lineEnd: 'none' | 'arrow' | 'tShape') => {
		setState(prev => ({ ...prev, lineEnd }))
	}, [])

	const setPathMode = useCallback((pathMode: 'sharp' | 'curve') => {
		setState(prev => ({ ...prev, pathMode }))
	}, [])

	const setHashAlignment = useCallback((hashAlignment: HashAlignment) => {
		setState(prev => ({ ...prev, hashAlignment }))
	}, [])

	const setShowColorPicker = useCallback((show: boolean) => {
		setState(prev => ({ ...prev, showColorPicker: show }))
	}, [])

	const setShowDrawOptions = useCallback((show: boolean) => {
		setState(prev => ({ ...prev, showDrawOptions: show }))
	}, [])

	return {
		state,
		updateState,
		setTool,
		setColor,
		setBrushSize,
		setLineStyle,
		setLineEnd,
		setPathMode,
		setHashAlignment,
		setShowColorPicker,
		setShowDrawOptions,
	}
}
